import { useInfiniteQuery } from 'react-query'
import { getJSON } from 'common/services/api'
import { Referral } from '../types'

interface ReferralsListResponse {
  success: boolean
  result: Referral[]
  count: number
}

interface UseReferralsListParams {
  residentId: string
  status?: 'active' | 'archived'
  limit?: number
}

interface UseReferralsListReturn {
  referrals: Referral[]
  isLoading: boolean
  refetch: () => void
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

export const useReferralsList = ({
  residentId,
  status,
  limit = 100
}: UseReferralsListParams): UseReferralsListReturn => {
  const fetchReferrals = async ({ pageParam = 0 }) => {
    let url = `/api/list/referrals?resident=${residentId}&limit=${limit}&offset=${pageParam}`

    if (status) {
      url += `&status=${status}`
    }

    const response = (await getJSON({ url })) as ReferralsListResponse

    return {
      referrals: response.result || [],
      totalCount: response.count || 0,
      nextOffset:
        response.result?.length === limit ? pageParam + limit : undefined
    }
  }

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(['referrals', residentId, status], fetchReferrals, {
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled: !!residentId
  })

  const referrals = data?.pages.flatMap((page) => page.referrals) || []

  return {
    referrals,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage
  }
}
